import { useMemo, useState } from "react";
import HeatScoreLabel from "@vms/HeatScoreLabel";
import MacStageLabel from "@vms/MacStageLabel";
import { getItemTileMetricColor, MetricBase } from "@core/models/Metric";
import { FCX, getRem, Tooltip } from "@datassential/platform-ui-lib";
import cn from "classnames";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import HeatScoreTooltip from "@vms/HeatScoreTooltip";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Skeleton, { SkeletonShape } from "@components/Skeleton";
import { CommonDataKeywordItem } from "@api/useCommonData";
import { useCategory } from "@core/CommonDataContext";
import { Toggle, ToggleType } from "@components/Toggle";
import { useSelectedKeywordsContext } from "@vms/SelectedKeywords/SelectedKeywordsContext";
import { Link } from "react-router-dom";
import { IoFastFoodOutline } from "react-icons/io5";

import { getKeywordProfileLink } from "@core/routes";

export const ITEM_TILE_DEFAULT_HEIGHT = 375

type ItemTileData =
    Pick<CommonDataKeywordItem, 'id' | 'description' | 'name' | 'macStage' | 'heatScore' | 'knowItChange' | 'thumbnailUrl' | 'primaryCategoryId'>
    & {
    metrics: MetricBase[]
}

type ItemTileProps = FCX<{
    item?: ItemTileData;
    tileHeight?: number;
    isMinimized?: boolean;
    isLoading?: boolean;
    isSelectionEnabled?: boolean;
}>;

const ItemTile: ItemTileProps = ({
    item,
    className,
    testId,
    isMinimized,
    tileHeight,
    isLoading = false,
    isSelectionEnabled,
    ...props
}) => {
    const [isImgError, setIsImgError] = useState(false);
    const category = useCategory(item?.primaryCategoryId)
    const {
        selectedKeywordsIds,
        toggleKeywordSelection,
    } = useSelectedKeywordsContext();

    const metricItems = useMemo(() => {
        if (isLoading) {
            // Render skeleton for 3 metrics
            return Array.from({ length: 3 }, (_, index) => (
                <div
                    key={`itemTileMetric-skeleton-${index}`}
                    className="ItemTileMetrics__item"
                >
                    <Skeleton
                        className="ItemTileMetrics__item-label"
                        shape={SkeletonShape.Text}
                        height={38}
                        radius={4}
                    />
                </div>
            ));
        }

        const itemMetrics = item?.metrics ?? [];
        return itemMetrics?.map((metric, index: number) => {
            return (
                <div
                    key={`itemTileMetric-${metric.id}`}
                    className="ItemTileMetrics__item "
                >
                    <div
                        className="ItemTileMetrics__item-value"
                        style={{ color: getItemTileMetricColor(metric.id) }}
                        data-testid={`textItemTileMetricValue${index}`}
                    >
                        {metric.value}%
                    </div>
                    <div
                        className="ItemTileMetrics__item-label"
                        data-testid={`textItemTileMetricLabel${index}`}
                    >
                        {metric.name}
                    </div>
                </div>
            )
        })
    }, [isLoading, item?.metrics]);

    const heatScoreTooltipId = useMemo(() => `keywordHeatScoreTooltip${item?.id}`, [item?.id])
    const descriptionTooltipId = useMemo(() => `keywordDescriptionTooltip${item?.id}`, [item?.id])

    const isSelected = useMemo(() => {
        return !!item?.id && selectedKeywordsIds.includes(item.id)
    }, [selectedKeywordsIds, item?.id])

    return (
        <div
            className={cn(
                "ItemTile",
                isSelectionEnabled && !isLoading && 'is-selectable',
                isSelectionEnabled && isSelected && 'is-selected',
                className,
            )}
            data-testid={testId}
        >
            <ContentWidget
                className={"ItemTile__content-widget"}
                style={tileHeight ? { maxHeight: tileHeight } : {}}
                {...props}
            >
                <div
                    className="ItemTile__content"
                    data-testid="blockItemTileContent"
                >
                    <div className="ItemTile__title-container">
                        <h3
                            className="ItemTile__title"
                            data-testid="textItemTileTitle"
                        >
                            {isLoading ? (
                                <Skeleton
                                    shape={SkeletonShape.Text}
                                    height={30}
                                    radius={4}
                                />
                            ) : (
                                <Link
                                    to={getKeywordProfileLink(item?.id)}
                                    className="ItemTile__link"
                                    data-tooltip-id={descriptionTooltipId}
                                >
                                    {item?.name ?? ""}
                                </Link>
                            )}
                        </h3>
                        {
                            !isMinimized && (
                                <span
                                    className="ItemTile__category"
                                    data-testid="textItemTileCategory"
                                >
                                {!isLoading && `Category: ${category?.name ?? ""}`}
							</span>
                            )
                        }
                        {isSelectionEnabled && !isLoading && !isMinimized && (
                            <Toggle
                                modifiers={["inverted"]}
                                className="ItemTile__checkbox"
                                type={ToggleType.RoundCheckbox}
                                isChecked={isSelected}
                                onChange={() => item?.id && toggleKeywordSelection(item.id)}
                            />

                        )}
                    </div>
                    {!isMinimized && (
                        isLoading ? (
                            <Skeleton
                                shape={SkeletonShape.Rect}
                                height={160}
                                radius={8}
                            />
                        ) : (
                            <Link
                                to={getKeywordProfileLink(item?.id)}
                                className="ItemTile__link"
                            >
                                {!isImgError && (
                                    <img
                                        className="ItemTile__image"
                                        src={item?.thumbnailUrl}
                                        alt={item?.name}
                                        data-testid="imageItemTileImage"
                                        loading="lazy"
                                        onError={() => setIsImgError(true)}
                                    />
                                )}
                                {isImgError && (
                                    <div className="ItemTile__image is-placeholder">
                                        <IoFastFoodOutline />
                                    </div>
                                )}
                            </Link>
                        )
                    )}
                    <div className="ItemTile__metrics ItemTileMetrics">
                        {metricItems}
                    </div>
                    {!isMinimized && (
                        <div className="ItemTile__footer">
                            <div className="ItemTile__footer-content">
                                <div className="ItemTile__footer-content-item">
                                    <div className="ItemTile__footer-label">
                                        {!isLoading && (
                                            <AiOutlineInfoCircle
                                                data-tooltip-id={heatScoreTooltipId}
                                                className="ItemTile__info-icon"
                                                data-testid="buttonTrendSummaryInfoIcon"
                                            />
                                        )}
                                        {isLoading ? (
                                            <Skeleton
                                                shape={SkeletonShape.Text}
                                                width={100}
                                                height={40}
                                                radius={4}
                                            />
                                        ) : (
                                            <span className="ItemTile__footer-label-text">Heat Score</span>
                                        )}
                                    </div>
                                    {!isLoading && (
                                        item?.heatScore ? (
                                            <HeatScoreLabel
                                                modifiers={["medium"]}
                                                heatScoreId={item.heatScore}
                                                testId="textItemTileHeatScore"
                                            />
                                        ) : <span className="ItemTile__footer-label-placeholder">-</span>
                                    )}
                                </div>
                                <div className="ItemTile__footer-content-item">
                                    {isLoading ? (
                                        <Skeleton
                                            shape={SkeletonShape.Text}
                                            width={100}
                                            height={40}
                                            radius={4}
                                        />
                                    ) : (
                                        <span className="ItemTile__footer-label-text">MAC Stage</span>
                                    )}
                                    {!isLoading && (
                                        item?.macStage ? (
                                            <MacStageLabel
                                                modifiers={["medium"]}
                                                macStageId={item.macStage}
                                                testId="textItemTileMacStage"
                                            />
                                        ) : <span className="ItemTile__footer-label-placeholder">-</span>
                                    )}
                                </div>
                            </div>
                            {!isLoading && (
                                <>
                                    <HeatScoreTooltip
                                        id={heatScoreTooltipId}
                                        place={"top-start"}
                                        isMinimized
                                        change={item?.knowItChange}
                                    />
                                    {item?.description && (
                                        <Tooltip
                                            id={descriptionTooltipId}
                                            place={"top-start"}
                                            style={{
                                                maxWidth: getRem((300)),
                                            }}
                                        >
                                            {item?.description}
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </ContentWidget>
        </div>
    );
};

export default ItemTile;
